import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { useLanguage } from '../shared/LanguageContext'
import PortalHeader from '../shared/PortalHeader'
import { fetchOfficeHours, mutateOfficeHours } from './officeHoursApi'
import { officeHoursText } from './officeHoursCopy'
import { newOfficeHoursMutationId, officeHoursTime, safeOfficeHoursLink } from './officeHoursFormat'
import { loadOfficeHoursPending, saveOfficeHoursPending } from './officeHoursPending'
import './officeHours.css'

function readSelection(studentIds) {
  const hash = new URLSearchParams(window.location.hash.slice(1))
  const candidate = hash.get('student')
  return { studentId: studentIds.includes(candidate) ? candidate : studentIds[0] || null, blockId: hash.get('block') || '' }
}

export default function OfficeHours() {
  const { studentIds, portalToken } = useAuth()
  // Remount every bit of booking state on a token handoff, including child labels.
  return <OfficeHoursSession key={portalToken} studentIds={studentIds} token={portalToken} />
}

function OfficeHoursSession({ studentIds, token }) {
  const ids = studentIds.filter(id => typeof id === 'string')
  const idsKey = JSON.stringify(ids)
  const [selection, setSelection] = useState(() => readSelection(ids))
  const [names, setNames] = useState({})
  // Returning to a child after an uncertain write recovers the original request.
  const pendingByChild = useRef(null)
  if (!pendingByChild.current) pendingByChild.current = loadOfficeHoursPending(token, ids)
  const { language } = useLanguage()
  const text = (key) => officeHoursText(key, language)
  const activeId = ids.includes(selection.studentId) ? selection.studentId : ids[0] || null

  useEffect(() => {
    const sync = () => setSelection(readSelection(JSON.parse(idsKey)))
    window.addEventListener('popstate', sync)
    window.addEventListener('hashchange', sync)
    sync()
    return () => {
      window.removeEventListener('popstate', sync)
      window.removeEventListener('hashchange', sync)
    }
  }, [idsKey])

  const select = useCallback((studentId, blockId = '') => {
    const hash = new URLSearchParams({ student: studentId })
    if (blockId) hash.set('block', blockId)
    window.history.pushState({}, '', `${window.location.pathname}${window.location.search}#${hash}`)
    setSelection({ studentId, blockId })
  }, [])

  const updateNames = useCallback((students) => {
    const permitted = JSON.parse(idsKey)
    setNames(current => ({ ...Object.fromEntries(Object.entries(current).filter(([id]) => permitted.includes(id))),
      ...Object.fromEntries(students.filter(s => permitted.includes(s.id) && typeof s.name === 'string').map(s => [s.id, s.name])) }))
  }, [idsKey])

  return (
    <div className="portal-shell office-hours">
      <PortalHeader studentName={names[activeId]} />
      <nav className="child-switcher" aria-label={text('child')}>
        {ids.map(id => <button key={id} className={`child-switcher-btn ${id === activeId ? 'active' : ''}`}
          aria-pressed={id === activeId} onClick={() => select(id)}>{names[id] || `${text('child')} ${id}`}</button>)}
      </nav>
      {activeId && token ? <OfficeHoursChild key={activeId} studentId={activeId} token={token}
        blockId={selection.blockId} selectBlock={(id) => select(activeId, id)} onStudents={updateNames}
        pendingByChild={pendingByChild.current} /> : <p role="alert">{text('unavailable')}</p>}
    </div>
  )
}

function MeetingDetails({ block, text, language }) {
  const link = safeOfficeHoursLink(block.meeting_link)
  return <>
    <p><strong>{officeHoursTime(block.start_at, language, text('unknownTime'))}</strong></p>
    <p className="oh-meta">— {officeHoursTime(block.end_at, language, text('unknownTime'))}</p>
    <p className="oh-meta">{block.appointment_format} · {text(block.meeting_mode === 'online' ? 'online' : 'offline')}</p>
    {block.meeting_mode === 'online' && link && <p><a href={link} target="_blank" rel="noopener noreferrer">{text('meeting')}</a></p>}
    {block.meeting_mode === 'offline' && block.location && <p>{text('location')}: {block.location}</p>}
    {block.details && <p className="oh-meta">{block.details}</p>}
  </>
}

function reasonText(reason, text) {
  const keys = {
    block_unavailable: 'reserved', capacity_unavailable: 'reserved',
    primary_not_eligible: 'notEligible', student_not_eligible: 'notEligible', student_ineligible: 'notEligible',
    upcoming_primary_limit: 'upcomingLimit', primary_upcoming_limit: 'upcomingLimit', primary_limit: 'upcomingLimit',
    usage_unresolved: 'usageUnavailable', usage_unavailable: 'usageUnavailable', priority_unavailable: 'usageUnavailable',
    repeat_window_not_open: 'windowNotOpen', booking_window_closed: 'windowNotOpen', priority_window: 'windowNotOpen', repeat_window_closed: 'windowNotOpen',
    student_overlap: 'overlap',
  }
  return keys[reason] ? text(keys[reason]) : text('unavailable')
}

function OfficeHoursChild({ studentId, token, blockId, selectBlock, onStudents, pendingByChild }) {
  const { language } = useLanguage()
  const text = (key) => officeHoursText(key, language)
  const [snapshot, setSnapshot] = useState(null)
  const [readError, setReadError] = useState(null)
  const [loading, setLoading] = useState(true)
  const [draft, setDraft] = useState({ partner: '', otherName: '' })
  const [cancelDraft, setCancelDraft] = useState({ bookingId: '', reason: '' })
  const [pending, setPending] = useState(() => pendingByChild.get(studentId) || null)
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState(null)
  const alive = useRef(true)
  const readSequence = useRef(0)
  const mutationBusy = useRef(false)

  useEffect(() => {
    alive.current = true
    return () => { alive.current = false; readSequence.current += 1 }
  }, [])

  const refresh = useCallback(async () => {
    const sequence = ++readSequence.current
    setLoading(true)
    const result = await fetchOfficeHours(token, studentId)
    if (!alive.current || sequence !== readSequence.current) return
    if (result.error) {
      setReadError(result)
    } else {
      setSnapshot(result.data)
      setReadError(null)
      onStudents(result.data.students)
    }
    setLoading(false)
  }, [studentId, token, onStudents])

  useEffect(() => { void refresh() }, [refresh])

  useEffect(() => {
    // Re-read on a visible return and periodically so MCP changes are observable.
    let lastVisibleRead = 0
    const visibleRefresh = () => {
      if (document.visibilityState === 'hidden' || Date.now() - lastVisibleRead < 30_000) return
      lastVisibleRead = Date.now()
      void refresh()
    }
    const interval = window.setInterval(() => {
      if (document.visibilityState !== 'hidden') void refresh()
    }, 300_000)
    window.addEventListener('focus', visibleRefresh)
    document.addEventListener('visibilitychange', visibleRefresh)
    return () => {
      window.clearInterval(interval)
      window.removeEventListener('focus', visibleRefresh)
      document.removeEventListener('visibilitychange', visibleRefresh)
    }
  }, [refresh])

  const setPendingRequest = (request) => {
    if (request) pendingByChild.set(studentId, request)
    else pendingByChild.delete(studentId)
    saveOfficeHoursPending(token, pendingByChild)
    setPending(request)
  }

  async function submit(request) {
    if (mutationBusy.current || !alive.current) return
    mutationBusy.current = true
    setBusy(true)
    setNotice(null)
    setPendingRequest(request)
    const result = await mutateOfficeHours(token, request.operation, request.payload, request.id)
    if (!alive.current) {
      // The returning scope may safely replay this same logical request.
      if (!result.unknown) pendingByChild.delete(studentId)
      return
    }
    mutationBusy.current = false
    setBusy(false)
    if (result.unknown) {
      setNotice({ key: 'unknown', code: result.code })
      void refresh()
      return
    }
    setPendingRequest(null)
    if (result.error) {
      setNotice({ key: result.status === 409 ? 'conflict' : 'unavailable', code: result.code })
      void refresh()
      return
    }
    const booking = result.data.data?.booking
    if (booking?.primary_student_id === studentId) {
      // Show only an exact committed, child-scoped reservation while refetching.
      setSnapshot(current => current ? { ...current, bookings: [booking, ...current.bookings.filter(b => b.id !== booking.id)] } : current)
    }
    setNotice({ key: 'committed' })
    setCancelDraft({ bookingId: '', reason: '' })
    void refresh()
  }

  const child = snapshot?.students.find(s => s.id === studentId)
  const partners = Array.isArray(snapshot?.partners?.[studentId]) ? snapshot.partners[studentId].filter(p => p.id !== studentId) : []
  const bookings = snapshot?.bookings.filter(b => b.primary_student_id === studentId) || []
  const blocks = snapshot?.blocks || []
  const selected = blocks.find(b => b.id === blockId)
  const recoveryBlock = pending?.operation === 'reserve' ? blocks.find(b => b.id === pending.payload.block_id)
    : bookings.find(b => b.id === pending?.payload.booking_id)?.block
  const canWrite = Boolean(snapshot && !readError && !loading && !busy && !pending)
  const policy = selected?.booking_policy?.[studentId]
  const selectedAllowed = canWrite && policy?.can_reserve === true
  const pair = selected?.appointment_format === '1:2'
  const partnerValid = !pair || (draft.partner === 'other' ? Boolean(draft.otherName.trim()) : partners.some(p => p.id === draft.partner))

  function reserve(event) {
    event.preventDefault()
    if (!selectedAllowed || !partnerValid) return
    const payload = { block_id: selected.id, primary_student_id: studentId }
    if (pair && draft.partner === 'other') {
      payload.partner_source = 'other'
      payload.other_partner_name = draft.otherName.trim()
    } else if (pair) {
      payload.partner_source = 'dropdown'
      payload.partner_student_id = draft.partner
    }
    try {
      void submit({ id: newOfficeHoursMutationId(), operation: 'reserve', payload })
    } catch {
      setNotice({ key: 'unavailable', code: 'secure_identity_unavailable' })
    }
  }

  function cancel(event, booking) {
    event.preventDefault()
    if (!canWrite || booking.can_cancel !== true || !cancelDraft.reason.trim()) return
    try {
      void submit({ id: newOfficeHoursMutationId(), operation: 'cancel', payload: {
        booking_id: booking.id, expected_revision: booking.revision, reason: cancelDraft.reason.trim(),
      } })
    } catch {
      setNotice({ key: 'unavailable', code: 'secure_identity_unavailable' })
    }
  }

  return <main className="portal-content" aria-busy={loading || busy}>
    <section>
      <div className="oh-toolbar"><h2>{text('title')}</h2><button className="oh-button" disabled={loading || busy} onClick={() => void refresh()}>{text('refresh')}</button></div>
      <p>{text('introduction')}</p>
      <p className="oh-meta">{text('timezone')}</p>
      <p className="oh-policy">{text('policy')}</p>
      <p className="oh-policy">{text('cancelPolicy')}</p>
    </section>
    {readError && <div className="oh-notice" role="alert"><p>{text(snapshot ? 'stale' : 'unavailable')}</p><span className="oh-error-code">{readError.code}</span></div>}
    {loading && !snapshot && <p role="status">{text('loading')}</p>}
    {notice && <div className="oh-status" role="status"><p>{text(notice.key)}</p>{notice.code && <span className="oh-error-code">{notice.code}</span>}</div>}
    {pending && <div className="oh-notice" role="alert"><p>{text('unknown')}</p>
      {recoveryBlock && <MeetingDetails block={recoveryBlock} text={text} language={language} />}
      {pending.payload.other_partner_name && <p>{text('partner')}: {pending.payload.other_partner_name}</p>}
      <button className="oh-button oh-primary" disabled={busy} onClick={() => void submit(pending)}>{busy ? text('saving') : text('recover')}</button></div>}
    {snapshot && <>
      {child?.eligible !== true && <p className="oh-notice">{text('notEligible')}</p>}
      {snapshot.usage?.[studentId]?.unresolved === true && <p className="oh-notice">{text('usageUnavailable')}</p>}
      <section aria-label={text('appointments')}><h3>{text('appointments')}</h3>
        {!bookings.length && !readError && <p className="oh-meta">{text('emptyBookings')}</p>}
        <div className="oh-list">{bookings.map(booking => {
          const block = booking.block || blocks.find(b => b.id === booking.block_id)
          const state = { pending: 'pending', confirmed: 'confirmed', expired: 'expired', rejected: 'rejected', cancelled: 'cancelled', completed: 'completed' }[booking.state]
          const partnerName = booking.partner_name || partners.find(p => p.id === booking.partner_student_id)?.name || booking.other_partner_name
          return <article className="oh-card" key={booking.id} data-booking-id={booking.id}>
            <header><strong>{child?.name || studentId}</strong><span className="oh-pill">{state ? text(state) : booking.state}</span></header>
            {block && <MeetingDetails block={block} text={text} language={language} />}
            {partnerName && <p>{text('partner')}: {partnerName}</p>}
            {booking.hold_expires_at && state === 'pending' && <p>{text('holdUntil')}: {officeHoursTime(booking.hold_expires_at, language, text('unknownTime'))}</p>}
            {block?.calendar?.state && block.calendar.state !== 'synced' && <p className="oh-meta">{text('calendarPending')}</p>}
            {booking.can_cancel === true && cancelDraft.bookingId !== booking.id && <button className="oh-button" disabled={!canWrite} onClick={() => setCancelDraft({ bookingId: booking.id, reason: '' })}>{text('cancel')}</button>}
            {cancelDraft.bookingId === booking.id && <form className="oh-form" onSubmit={event => cancel(event, booking)}>
              <p className="oh-policy">{text(booking.cancellation_usage_effect === 'counts_primary_use' ? 'countsPrimaryUse' : booking.cancellation_usage_effect === 'preserves_unused' ? 'preservesUnused' : 'cancelPolicy')}</p>
              <label>{text('cancelReason')}<textarea required maxLength={2000} disabled={busy || Boolean(pending)} value={cancelDraft.reason} onChange={event => setCancelDraft({ bookingId: booking.id, reason: event.target.value })} /></label>
              <div className="oh-actions"><button className="oh-button oh-primary" disabled={!canWrite || !cancelDraft.reason.trim()}>{text('confirmCancel')}</button><button className="oh-button" type="button" disabled={busy || Boolean(pending)} onClick={() => setCancelDraft({ bookingId: '', reason: '' })}>{text('keep')}</button></div>
            </form>}
          </article>
        })}</div>
      </section>
      <section aria-label={text('available')}><h3>{text('available')}</h3>
        {!blocks.length && !readError && <p className="oh-meta">{text('emptyBlocks')}</p>}
        <div className="oh-list">{blocks.map(block => {
          const bookingPolicy = block.booking_policy?.[studentId]
          return <article className={`oh-card ${block.id === blockId ? 'oh-card-selected' : ''}`} key={block.id} data-block-id={block.id}>
            <MeetingDetails block={block} text={text} language={language} />
            {bookingPolicy?.can_reserve !== true && <p className="oh-meta">{reasonText(bookingPolicy?.reason, text)}</p>}
            {bookingPolicy?.can_reserve === false && ['repeat_window_closed', 'priority_unavailable'].includes(bookingPolicy.reason)
              && bookingPolicy.window_opens_at && <p className="oh-meta">{text('opensAt')}: {officeHoursTime(bookingPolicy.window_opens_at, language, text('unknownTime'))}</p>}
            <button className="oh-button" aria-pressed={block.id === blockId} disabled={busy || Boolean(pending)} onClick={() => selectBlock(block.id)}>{text(block.id === blockId ? 'selected' : 'select')}</button>
            {block.id === blockId && <form className="oh-form" onSubmit={reserve}>
              {pair && <>
                <label>{text('partner')}<select required disabled={busy || Boolean(pending)} value={draft.partner} onChange={event => setDraft(current => ({ ...current, partner: event.target.value }))}>
                  <option value="">{text('choosePartner')}</option>
                  {partners.map(partner => <option key={partner.id} value={partner.id}>{partner.name}</option>)}
                  <option value="other">{text('other')}</option>
                </select></label>
                {draft.partner === 'other' && <><label>{text('otherName')}<input required maxLength={200} disabled={busy || Boolean(pending)} value={draft.otherName} onChange={event => setDraft(current => ({ ...current, otherName: event.target.value }))} /></label><p className="oh-policy">{text('approval')}</p></>}
              </>}
              <button className="oh-button oh-primary" disabled={!selectedAllowed || !partnerValid}>{busy ? text('saving') : text(pair && draft.partner === 'other' ? 'request' : 'book')}</button>
            </form>}
          </article>
        })}</div>
      </section>
    </>}
    <Link to="/parent">{text('returnPortal')}</Link>
  </main>
}
